import React, { useMemo } from 'react';
import { Autocomplete, Box, TextField, Typography, createFilterOptions } from '@mui/material';

const obterId = (opcao) => (opcao?.id == null ? '' : String(opcao.id));
const obterDescricao = (opcao) => String(opcao?.descricao ?? '');

// A pesquisa é local: utiliza a mesma lista já carregada pelo cadastro rápido.
const filtrarEmbalagens = createFilterOptions({
    ignoreAccents: true,
    ignoreCase: true,
    matchFrom: 'any',
    trim: true,
    stringify: obterDescricao,
});

/**
 * Seletor pesquisável, restrito às embalagens recebidas em options.
 * Preserva o contrato de actions.change: target.name + target.value (ID).
 * O texto digitado serve apenas para pesquisa; nunca é enviado como ID.
 */
export default function EmbalagemXmlAutocomplete({ text, name, options = [], value = '', handleOnChange, disabled = false }) {
    const opcoes = useMemo(
        () => (Array.isArray(options) ? options.filter((opcao) => obterId(opcao).trim() !== '') : []),
        [options]
    );

    const selecionada = useMemo(() => opcoes.find((opcao) => obterId(opcao) === String(value ?? '')) ?? null, [opcoes, value]);

    const selecionarEmbalagem = (_event, opcao) => {
        if (disabled) return;

        // Igual ao select anterior: mantém o nome do campo e envia ID ou vazio.
        handleOnChange?.({
            target: {
                name,
                value: obterId(opcao),
                type: 'select-one',
            },
        });
    };

    return (
        <Autocomplete
            id={`cadastro-rapido-xml-${name}`}
            options={opcoes}
            value={selecionada}
            onChange={selecionarEmbalagem}
            getOptionLabel={obterDescricao}
            getOptionKey={obterId}
            isOptionEqualToValue={(opcao, selecionadaAtual) =>
                obterId(opcao) !== '' && obterId(opcao) === obterId(selecionadaAtual)
            }
            filterOptions={filtrarEmbalagens}
            freeSolo={false}
            autoSelect={false}
            clearOnBlur
            selectOnFocus
            openOnFocus
            disabled={disabled}
            fullWidth
            size="small"
            noOptionsText="Nenhuma embalagem encontrada."
            clearText="Limpar embalagem"
            openText="Pesquisar embalagens"
            closeText="Fechar lista"
            slotProps={{ listbox: { sx: { maxHeight: 240 } } }}
            sx={{ minWidth: 0 }}
            renderOption={(props, opcao) => {
                const { key, ...optionProps } = props;

                return (
                    <li key={key} {...optionProps}>
                        <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                            <Typography variant="body2">{obterDescricao(opcao)}</Typography>
                            {opcao?.fator != null && opcao.fator !== '' && (
                                <Typography variant="caption" color="text.secondary">
                                    Fator: {opcao.fator}
                                </Typography>
                            )}
                        </Box>
                    </li>
                );
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    name={name}
                    label={text}
                    placeholder="Digite para pesquisar a embalagem"
                    title="Pesquise pela descrição e selecione uma embalagem da lista"
                />
            )}
        />
    );
}
